import Link from 'next/link'
import { useMemo } from 'react'
import { UserIcon } from 'lucide-react'

import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { SIGN_IN_PATH, SIGN_UP_PATH } from '@/constants/authRoutes'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { cn } from '@/utilities/ui'

import { NavUserSkeleton } from './NavUserSkeleton'

/**
 * Header account controls. The user is loaded after mount so page renders stay static.
 */
export const NavUser: React.FC<{
  colorInverse: boolean
  isMenuOpen: boolean
  LINK_CLASSES: string
}> = ({ colorInverse, isMenuOpen, LINK_CLASSES }) => {
  const payloadUser = useCurrentUser()

  const LINK_ICON_CLASSES = useMemo(() => {
    return cn(
      'flex size-11! shrink-0 items-center justify-center overflow-hidden rounded-2xl lg:size-10!',
      payloadUser?.image
        ? colorInverse
          ? 'border border-border-inverse focus-visible:ring-evergreen-300!'
          : 'border border-border'
        : colorInverse
          ? 'text-white hover:bg-accent/25 hover:text-white focus-visible:ring-evergreen-300!'
          : 'bg-neutral-200 text-neutral-900 hover:bg-accent-foreground/50 hover:text-neutral-900',
      !payloadUser?.image && colorInverse && !isMenuOpen && 'bg-primary text-primary-foreground',
    )
  }, [colorInverse, isMenuOpen, payloadUser])

  if (payloadUser === undefined) {
    return <NavUserSkeleton />
  }

  return (
    <>
      <Button
        variant="link"
        icon={false}
        asChild
        className={cn(
          payloadUser ? LINK_ICON_CLASSES : LINK_CLASSES,
          isMenuOpen && 'text-neutral-900',
        )}
      >
        {payloadUser ? (
          <Link href="/dashboard" title="Visit your dashboard">
            <span className="sr-only">Visit your dashboard</span>
            {payloadUser.image ? (
              <Media
                resource={payloadUser.image}
                width={44}
                height={44}
                className="size-11 object-cover object-center lg:size-10"
                pictureClassName="size-11 object-cover object-center lg:size-10"
                imgClassName={cn('size-11 object-cover object-center lg:size-10')}
              />
            ) : (
              <UserIcon className="size-6" />
            )}
          </Link>
        ) : (
          <Link href={SIGN_UP_PATH}>Sign Up</Link>
        )}
      </Button>
      {!payloadUser && (
        <Button
          variant="link"
          icon={false}
          asChild
          className={cn(
            payloadUser ? LINK_ICON_CLASSES : LINK_CLASSES,
            isMenuOpen && 'text-neutral-900',
          )}
        >
          <Link href={SIGN_IN_PATH}>Sign In</Link>
        </Button>
      )}
    </>
  )
}
